import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import type { AuditFormData } from '@/pages/audits/CreateAudit';
import { dataService } from '@/services/data-service';
import { useMode } from '@/providers/ModeProvider';
import type { Asset } from '@/types/asset-audit';
import type { Location, Category } from '@/types/settings';
import { AssetSelectionDialog } from '@/components/depreciation/asset-selection-dialog';
import { Building2, FolderTree, List, Globe, Check, ChevronsUpDown, X, Package } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DefineScopeStepProps {
  formData: AuditFormData;
  updateFormData: (updates: Partial<AuditFormData>) => void;
}

const SCOPE_TYPES = [
  {
    value: 'all' as const,
    label: 'All Assets',
    description: 'Include all assets in the system',
    icon: Globe,
  },
  {
    value: 'location' as const,
    label: 'By Location',
    description: 'Select a specific location',
    icon: Building2,
  },
  {
    value: 'category' as const,
    label: 'By Category',
    description: 'Select an asset category',
    icon: FolderTree,
  },
  {
    value: 'custom' as const,
    label: 'Custom List',
    description: 'Manually select assets',
    icon: List,
  },
];

export function DefineScopeStep({ formData, updateFormData }: Readonly<DefineScopeStepProps>) {
  const { mode } = useMode();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [showAssetSelection, setShowAssetSelection] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [estimatedCount, setEstimatedCount] = useState(0);

  useEffect(() => {
    loadData();
  }, [mode]);

  useEffect(() => {
    calculateEstimatedCount();
  }, [formData.scopeType, formData.locationIds, formData.departmentIds, formData.categoryIds, formData.assetIds, assets]);

  // Sync selected assets with formData.assetIds whenever assets or assetIds change
  useEffect(() => {
    if (assets.length > 0 && formData.assetIds.length > 0) {
      // Convert both to strings for comparison to handle type mismatches
      const assetIdStrings = formData.assetIds.map(id => String(id));
      const selected = assets.filter(asset => {
        return assetIdStrings.includes(String(asset.id));
      });
      setSelectedAssets(selected);
    } else if (formData.assetIds.length === 0) {
      setSelectedAssets([]);
    }
  }, [assets, formData.assetIds]);

  const loadData = async () => {
    try {
      setLoading(true);
      setAssets([]);
      
      let assetsResult: { data: Asset[] };
      
      if (mode === 'demo') {
        // In demo mode, use demo data directly
        const demoAssets = dataService.getDemoAssets({ page: 1, pageSize: 10000 });
        assetsResult = { data: demoAssets.data as unknown as Asset[] };
      } else {
        // In live mode, call API only - fall back to demo on error
        try {
          const apiAssets = await dataService.getAssetsApiOnly({ page: 1, pageSize: 10000 });
          assetsResult = { data: apiAssets.data as unknown as Asset[] };
        } catch (apiError) {
          console.warn('Assets API failed, falling back to demo:', apiError);
          const demoAssets = dataService.getDemoAssets({ page: 1, pageSize: 10000 });
          assetsResult = { data: demoAssets.data as unknown as Asset[] };
        }
      }
      
      // Load locations and categories (these don't have the same issue)
      const [locationsData, categoriesData] = await Promise.all([
        dataService.getLocations(),
        dataService.getCategories(),
      ]);
      
      const loadedAssets = assetsResult.data ?? [];
      setAssets(loadedAssets);
      setLocations(locationsData);
      setCategories(categoriesData);
      
      // The sync useEffect will handle populating selectedAssets from formData.assetIds
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateEstimatedCount = () => {
    if (formData.scopeType === 'all') {
      setEstimatedCount(assets.length);
    } else if (formData.scopeType === 'location') {
      const count = assets.filter(a => a.locationId && formData.locationIds.includes(a.locationId)).length;
      setEstimatedCount(count);
    } else if (formData.scopeType === 'category') {
      const count = assets.filter(a => a.categoryId && formData.categoryIds.includes(a.categoryId)).length;
      setEstimatedCount(count);
    } else if (formData.scopeType === 'custom') {
      setEstimatedCount(formData.assetIds.length);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <Card className="bg-card border rounded-lg shadow-sm">
      <CardContent className="pt-6">
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-4">Define Audit Scope</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Select which assets should be included in this audit.
            </p>
          </div>

      {/* Scope Type Selection */}
      <div className="space-y-3">
        <Label>
          Scope Type <span className="text-red-500">*</span>
        </Label>
        <RadioGroup
          value={formData.scopeType}
          onValueChange={(value: string) => updateFormData({ 
            scopeType: value as AuditFormData['scopeType'],
            locationIds: [],
            departmentIds: [],
            categoryIds: [],
            assetIds: [],
          })}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {SCOPE_TYPES.map((scope) => {
              const Icon = scope.icon;
              const isSelected = formData.scopeType === scope.value;
              return (
                <label
                  key={scope.value}
                  htmlFor={`scope-${scope.value}`}
                  className={cn(
                    'flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all',
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  )}
                >
                  <RadioGroupItem value={scope.value} id={`scope-${scope.value}`} className="mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={cn('h-4 w-4', isSelected && 'text-primary')} />
                      <span className="font-medium text-sm">{scope.label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{scope.description}</p>
                  </div>
                </label>
              );
            })}
          </div>
        </RadioGroup>
      </div>

      {/* Conditional Selectors */}
      {formData.scopeType === 'location' && (
        <div className="space-y-3">
          <Label>Select Location</Label>
          <Popover open={locationOpen} onOpenChange={setLocationOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={locationOpen}
                className="w-full justify-between bg-white"
              >
                {formData.locationIds.length > 0
                  ? locations.find((loc) => loc.id === formData.locationIds[0])?.name
                  : "Select location..."}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start">
              <Command>
                <CommandInput placeholder="Search locations..." />
                <CommandList>
                  <CommandEmpty>No location found.</CommandEmpty>
                  <CommandGroup>
                    {locations.map((location) => (
                      <CommandItem
                        key={location.id}
                        value={location.name}
                        onSelect={() => {
                          updateFormData({ locationIds: [location.id] });
                          setLocationOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            formData.locationIds.includes(location.id) ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {location.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {formData.locationIds.length > 0 && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="secondary" className="gap-1">
                {locations.find((loc) => loc.id === formData.locationIds[0])?.name}
                <X
                  className="h-3 w-3 cursor-pointer"
                  onClick={() => updateFormData({ locationIds: [] })}
                />
              </Badge>
            </div>
          )}
        </div>
      )}

      {formData.scopeType === 'category' && (
        <div className="space-y-3">
          <Label>Select Category</Label>
          <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={categoryOpen}
                className="w-full justify-between bg-white"
              >
                {formData.categoryIds.length > 0
                  ? (() => {
                      const cat = categories.find((c) => c.id === formData.categoryIds[0]);
                      return cat ? `${cat.code} - ${cat.description}` : "Select category...";
                    })()
                  : "Select category..."}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start">
              <Command>
                <CommandInput placeholder="Search categories..." />
                <CommandList>
                  <CommandEmpty>No category found.</CommandEmpty>
                  <CommandGroup>
                    {categories.map((category) => (
                      <CommandItem
                        key={category.id}
                        value={`${category.code} ${category.description}`}
                        onSelect={() => {
                          updateFormData({ categoryIds: [category.id] });
                          setCategoryOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            formData.categoryIds.includes(category.id) ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {category.code} - {category.description}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {formData.categoryIds.length > 0 && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="secondary" className="gap-1">
                {(() => {
                  const cat = categories.find((c) => c.id === formData.categoryIds[0]);
                  return cat ? `${cat.code} - ${cat.description}` : "";
                })()}
                <X
                  className="h-3 w-3 cursor-pointer"
                  onClick={() => updateFormData({ categoryIds: [] })}
                />
              </Badge>
            </div>
          )}
        </div>
      )}

      {formData.scopeType === 'custom' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Selected Assets</Label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAssetSelection(true)}
            >
              <List className="mr-2 h-4 w-4" />
              Select Assets
            </Button>
          </div>
          
          {formData.assetIds.length > 0 ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  {formData.assetIds.length} asset{formData.assetIds.length > 1 ? 's' : ''} selected
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedAssets([]);
                    updateFormData({ assetIds: [] });
                  }}
                >
                  Clear all
                </Button>
              </div>
              
              <div className="border rounded-md bg-white max-h-[400px] overflow-y-auto">
                <div className="divide-y">
                  {selectedAssets.map((asset) => (
                    <div
                      key={asset.id}
                      className="flex items-center justify-between p-3 hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {asset.imageUrl ? (
                          <img
                            src={asset.imageUrl}
                            alt={asset.name}
                            className="w-10 h-10 rounded object-cover flex-shrink-0"
                          />
                        ) : (
                        <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <Package className="h-5 w-5 text-gray-400" />
                        </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{asset.name}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {asset.code}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newAssets = selectedAssets.filter((a) => a.id !== asset.id);
                          setSelectedAssets(newAssets);
                          updateFormData({ assetIds: newAssets.map((a) => a.id) });
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="border rounded-md bg-gray-50 p-8 text-center">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                No assets selected. Click "Select Assets" to add assets to this audit.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Asset Selection Dialog */}
      <AssetSelectionDialog
        open={showAssetSelection}
        onOpenChange={setShowAssetSelection}
        onConfirm={(selectedAssets) => {
          const assetIds = selectedAssets.map((a) => a.id);
          // Convert from depreciation Asset type to audit Asset type
          const convertedAssets: Asset[] = selectedAssets.map((a) => ({
            id: a.id,
            code: a.code ?? '',
            name: a.name,
            assetTag: a.barcode,
            description: a.description,
            category: a.category,
            location: a.location,
            status: a.status,
          }));
          setSelectedAssets(convertedAssets);
          updateFormData({ assetIds });
          setShowAssetSelection(false);
        }}
        title="Select Assets for Audit"
        description="Choose one or more assets to include in this audit."
        initialSelectedAssetIds={formData.assetIds}
        confirmButtonLabel="Confirm Selection"
      />

      {/* Scope Summary - Only show for non-custom scope types */}
      {formData.scopeType !== 'custom' && (
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Estimated Asset Count</p>
              <p className="text-xs text-muted-foreground">Assets that will be included in this audit</p>
            </div>
            <Badge variant="secondary" className="text-lg px-4 py-2">
              {estimatedCount}
            </Badge>
          </div>
        </div>
      )}
        </div>
      </CardContent>
    </Card>
  );
}
